/**
 * Dynamic Credit Features Configuration
 * Uses live pricing data from the database instead of hardcoded values
 */

import { usePricingData, type PricingData, type TransformedPricingData } from '@/hooks/token/use-pricing-data';

export interface DynamicFeatureConfig {
  id: string;
  name: string;
  description: string;
  creditsPerUse: number;
  icon: string;
  app: string;
  category: 'generation' | 'research' | 'chat' | 'creation';
  examples: string[];
  color: 'blue' | 'purple' | 'green' | 'orange';
  serviceProvider: string;
  model: string;
  operation_type: string;
}

// Map operation types to feature configurations
const getFeatureConfigTemplate = (operationType: string): Omit<DynamicFeatureConfig, 'creditsPerUse' | 'serviceProvider' | 'model' | 'operation_type'> => {
  const configMap: Record<string, Omit<DynamicFeatureConfig, 'creditsPerUse' | 'serviceProvider' | 'model' | 'operation_type'>> = {
    'research': {
      id: 'research-generation',
      name: 'Research Generation',
      description: 'AI-powered research articles and summaries',
      icon: 'Search',
      app: 'Research Library',
      category: 'research',
      color: 'orange',
      examples: ['Market analysis', 'Competitor research', 'Industry reports']
    },
    'image_generation': {
      id: 'image-generation',
      name: 'Image Generation',
      description: 'AI-generated images and artwork',
      icon: 'Image',
      app: 'Content Generator',
      category: 'generation',
      color: 'orange',
      examples: ['Product images', 'Marketing visuals', 'Concept art']
    },
    'text_generation': {
      id: 'text-generation',
      name: 'Text Generation',
      description: 'AI writing assistance and content creation',
      icon: 'FileText',
      app: 'Content Generator',
      category: 'generation',
      color: 'orange',
      examples: ['Blog posts', 'Product descriptions', 'Email copy']
    },
    'threaded_generation': {
      id: 'task-recommendations',
      name: 'Recommended Tasks',
      description: 'AI-powered task recommendations and suggestions',
      icon: 'CheckSquare',
      app: 'Task Manager',
      category: 'generation',
      color: 'orange',
      examples: ['Strategy planning', 'Goal breakdown', 'Action items']
    },
    'agent_chat': {
      id: 'ai-chat',
      name: 'Chat Messages',
      description: 'Interactive conversations with AI assistants',
      icon: 'MessageCircle',
      app: 'AI Agents',
      category: 'chat',
      color: 'orange',
      examples: ['Strategy discussions', 'Problem solving', 'Brainstorming']
    }
  };

  return configMap[operationType] || {
    id: operationType,
    name: operationType.replace('_', ' '),
    description: `AI-powered ${operationType.replace('_', ' ')}`,
    icon: 'Zap',
    app: 'Platform',
    category: 'generation',
    color: 'blue',
    examples: [`${operationType} examples`]
  };
};

// Transform pricing data into feature configurations
export const transformPricingToFeatureConfig = (pricingData: PricingData[]): DynamicFeatureConfig[] => {
  // Filter to only show the 4 desired operation types and exclude agent_chat
  const allowedOperations = ['research', 'image_generation', 'text_generation', 'threaded_generation'];
  
  return pricingData
    .filter(item => allowedOperations.includes(item.operation_type))
    .map(item => {
      const template = getFeatureConfigTemplate(item.operation_type);
      return {
        ...template,
        creditsPerUse: Number(item.avg_credit_cost || 0),
        serviceProvider: item.service_provider,
        model: item.model,
        operation_type: item.operation_type,
      };
    });
};

// Hook to get dynamic feature configurations based on live pricing
export const useDynamicFeatureConfig = () => {
  const { data: pricingData, isLoading, error } = usePricingData();
  
  const featureConfigs = pricingData ? transformPricingToFeatureConfig(pricingData) : [];
  
  return {
    featureConfigs,
    isLoading,
    error,
    // Helper functions
    getFeatureById: (id: string) => featureConfigs.find(f => f.id === id),
    getFeatureByOperationType: (operationType: string) => featureConfigs.find(f => f.operation_type === operationType),
    calculateTotalUses: (credits: number) => {
      return featureConfigs.reduce((acc, feature) => {
        acc[feature.id] = feature.creditsPerUse > 0 ? Math.floor(credits / feature.creditsPerUse) : 0;
        return acc;
      }, {} as Record<string, number>);
    }
  };
};

// Preset credit amounts for quick selection (unchanged)
export const PRESET_CREDIT_AMOUNTS = [100, 500, 1000, 5000, 10000];

// Legacy support - dynamic version of CREDIT_FEATURES
export const createDynamicCreditFeatures = (pricingData: PricingData[]) => {
  return pricingData.reduce((acc, item) => {
    const template = getFeatureConfigTemplate(item.operation_type);
    acc[template.name] = {
      creditsPerUse: Number(item.avg_credit_cost || 0),
      description: template.description,
      example: template.examples.join(', '),
      appContext: template.app,
      color: template.color,
      serviceProvider: item.service_provider,
      model: item.model,
    };
    return acc;
  }, {} as Record<string, any>);
}; 